/**
 * Utilitários para calcular alertas e severidades do Smart Routing
 */

import { formatMoney } from "@/helpers/format"
import type {
  FailoverSeverity,
  ProgressSeverity,
  SmartRoutingAlerts,
  SmartRoutingConfiguration,
} from "@/types/smart-routing"

export const calculateFailoverSeverity = (
  failoverRate: number | null
): FailoverSeverity => {
  if (failoverRate === null) return "excellent"

  if (failoverRate >= 100) return "emergency"
  if (failoverRate >= 20) return "critical"
  if (failoverRate >= 10) return "high"
  if (failoverRate >= 5) return "acceptable"
  return "excellent"
}

export const calculateProgressSeverity = (
  progress: number | null
): ProgressSeverity => {
  if (progress === null) return "normal"

  if (progress >= 150) return "critical" // 150%+ do target
  if (progress >= 100) return "warning" // 100%+ do target
  return "normal"
}

export const calculateSmartRoutingAlerts = (
  configuration: SmartRoutingConfiguration
): SmartRoutingAlerts => {
  const { failoverRate, primary } = configuration

  const failoverSeverity =
    calculateFailoverSeverity(failoverRate)
  const progressSeverity = calculateProgressSeverity(
    primary.progress
  )

  const hasFailoverAlert =
    failoverSeverity === "critical" ||
    failoverSeverity === "emergency"
  const hasVolumeAlert = progressSeverity === "critical"

  return {
    failoverSeverity,
    progressSeverity,
    hasVolumeAlert,
    hasFailoverAlert,
  }
}

export const getFailoverSeverityColor = (
  severity: FailoverSeverity
): string => {
  const colors = {
    excellent: "#22c55e", // green-500
    acceptable: "#84cc16", // lime-500
    high: "#f97316", // orange-500
    critical: "#ef4444", // red-500
    emergency: "#dc2626", // red-600
  }

  return colors[severity]
}

export const getProgressSeverityColor = (
  severity: ProgressSeverity
): string => {
  const colors = {
    normal: "#22c55e", // green-500
    warning: "#f59e0b", // amber-500
    critical: "#ef4444", // red-500
  }

  return colors[severity]
}

export const getFailoverSeverityIcon = (
  severity: FailoverSeverity
): string => {
  const icons = {
    excellent: "✅",
    acceptable: "⚠️",
    high: "🔶",
    critical: "🔴",
    emergency: "🚨",
  }

  return icons[severity]
}

export const formatVolumeProgress = (
  volume: number | null,
  target: number | null,
  progress: number | null
): string => {
  if (
    volume === null ||
    target === null ||
    progress === null
  ) {
    return "—"
  }

  const volumeFormatted = formatMoney(volume / 100)
  const targetFormatted = formatMoney(target / 100)

  return `${volumeFormatted} / ${targetFormatted} (${progress.toFixed(1)}%)`
}
